import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { successToast, errorToast } from "../../utils/customToast";
import { i18n } from "../../translate/i18n";
import Autocomplete from "@material-ui/lab/Autocomplete";
import moment from "moment";
import { isArray } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import ModernConfirmationModal from "../ModernConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { Facebook, Instagram, WhatsApp, FlashOn, Schedule, Repeat, Person, Business, AttachFile } from "@material-ui/icons";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { Switch, FormControlLabel } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  stepper: {
    padding: theme.spacing(3, 0, 2),
    backgroundColor: "transparent",
  },
  stepContent: {
    padding: theme.spacing(2, 0),
    minHeight: "400px",
    maxHeight: "none",
  },
  stepTitle: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  stepSubtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: theme.spacing(2),
  },
  navigationButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #f0f0f0",
  },
  backButton: {
    borderRadius: "8px",
    padding: "10px 24px",
    fontWeight: 600,
    textTransform: "none",
  },
  nextButton: {
    borderRadius: "8px",
    padding: "10px 24px",
    fontWeight: 600,
    textTransform: "none",
    minWidth: "120px",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  btnWrapper: {
    position: "relative",
  },
}));

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, "Mensagem muito curta").required("Obrigatório"),
  contactId: Yup.number().required("Obrigatório"),
  sendAt: Yup.string().required("Obrigatório"),
});

const steps = [
  { label: () => i18n.t("scheduleModal.steps.recipient"), description: () => i18n.t("scheduleModal.steps.recipientDesc") },
  { label: () => i18n.t("scheduleModal.steps.message"), description: () => i18n.t("scheduleModal.steps.messageDesc") },
  { label: () => i18n.t("scheduleModal.steps.settings"), description: () => i18n.t("scheduleModal.steps.settingsDesc") }
];

const StepperScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  cleanContact,
  reload,
  message,
  fromMessageInput = false,
  user,
  selectedDate = null
}) => {
  console.log("🔄 StepperScheduleModal renderizado - open:", open, "scheduleId:", scheduleId);
  
  const classes = useStyles();
  const history = useHistory();
  const isMounted = useRef(true);
  const { companyId } = user;
  const isAdmin = user.profile === 'admin';
  const messageInputRef = useRef();
  const { findAll: findAllQueues } = useQueues();
  const filterOptions = createFilterOptions({
    trim: true,
  });

  // Estados do stepper
  const [activeStep, setActiveStep] = useState(0);

  // Estados do formulário
  const getInitialSendAt = useCallback(() => {
    if (selectedDate) {
      try {
        const selectedDateTime = new Date(selectedDate);
        const now = new Date();
        selectedDateTime.setHours(now.getHours() + 1);
        selectedDateTime.setMinutes(now.getMinutes());
        selectedDateTime.setSeconds(0);
        return moment(selectedDateTime).format("YYYY-MM-DDTHH:mm");
      } catch (error) {
        console.error("Erro ao processar selectedDate:", error);
        return moment().add(1, "hour").format("YYYY-MM-DDTHH:mm");
      }
    }
    return moment().add(1, "hour").format("YYYY-MM-DDTHH:mm");
  }, [selectedDate]);

  const initialState = useMemo(() => ({
    body: message || "",
    contactId: contactId || "",
    sendAt: getInitialSendAt(),
    sentAt: "",
    openTicket: "enabled",
    ticketUserId: user.id,
    queueId: "",
    statusTicket: "open",
    intervalo: 1,
    valorIntervalo: 0,
    enviarQuantasVezes: 1,
    tipoDias: 4,
    assinar: false,
    reminderDate: "",
  }), [message, contactId, getInitialSendAt, user.id]);

  const initialContact = {
    id: "",
    name: "",
    channel: "",
  };

  const [schedule, setSchedule] = useState(initialState);
  const [currentContact, setCurrentContact] = useState(initialContact);
  const [contacts, setContacts] = useState([initialContact]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState("");
  const [channelFilter, setChannelFilter] = useState("whatsapp");
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  // Estados para quickMessages
  const [quickMessages, setQuickMessages] = useState([]);
  const [filteredQuickMessages, setFilteredQuickMessages] = useState([]);
  const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);
  const [quickMessageSearch, setQuickMessageSearch] = useState("");
  const [showAllQuickMessages, setShowAllQuickMessages] = useState(false);

  // Estados para configurações avançadas
  const [intervalo, setIntervalo] = useState(1);
  const [tipoDias, setTipoDias] = useState(4);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  // Cleanup para evitar memory leaks
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Funções do stepper
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    onClose();
    setSchedule(initialState);
    setCurrentContact(initialContact);
    setActiveStep(0);
    // Reset das respostas rápidas
    setQuickMessageSearch("");
    setShowAllQuickMessages(false);
    setFilteredQuickMessages([]);
    // Reset do estado de erro de rede
    setNetworkError(false);
  };

  const isStepValid = (step, values) => {
    console.log("🔍 Validando step:", step, "Values:", values);
    
    switch (step) {
      case 0: // Destinatário
        const step0Valid = currentContact.id && values.sendAt;
        console.log("Step 0 - Contact:", currentContact.id, "SendAt:", values.sendAt, "Valid:", step0Valid);
        return step0Valid;
      case 1: // Mensagem
        const bodyValue = values.body || "";
        const step1Valid = bodyValue.trim().length >= 5;
        console.log("Step 1 - Body:", bodyValue, "Length:", bodyValue.length, "Valid:", step1Valid);
        return step1Valid;
      case 2: // Configurações
        // Validação mais restritiva: deve ter WhatsApp selecionado
        const hasWhatsapp = selectedWhatsapps && selectedWhatsapps !== "";
        console.log("Step 2 - WhatsApp:", selectedWhatsapps, "HasWhatsapp:", hasWhatsapp);
        
        // Só é válido se tiver WhatsApp selecionado
        // Isso garante que o botão "Agendar" só fica habilitado quando configurado
        return hasWhatsapp;
      default:
        return false;
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    if (open) {
      const loadData = async () => {
        try {
          console.log("🔄 Carregando dados iniciais...");
          
          // Carregar contatos
          const { data: contactList } = await api.get("/contacts/list", {
            params: { companyId: companyId },
          });

          // Verificar se o componente ainda está montado
          if (!isMounted.current) return;

          let customList = contactList.map((c) => ({
            id: c.id,
            name: c.name,
            channel: c.channel,
          }));

          if (isArray(customList)) {
            setContacts([{ id: "", name: "", channel: "" }, ...customList]);
          }

          console.log("✅ Contatos carregados:", customList.length);

          // Se contactId foi fornecido, definir contato atual
          if (contactId && customList.length > 0) {
            const foundContact = customList.find((c) => c.id.toString() === contactId.toString());
            if (foundContact) {
              setCurrentContact(foundContact);
              setChannelFilter(foundContact.channel || "whatsapp");
            }
          }

          // Carregar filas
          const queueList = await findAllQueues();
          
          // Verificar se o componente ainda está montado
          if (!isMounted.current) return;
          
          setAllQueues(queueList);
          setQueues(queueList);
          console.log("✅ Filas carregadas:", queueList.length);

          // Se editando agendamento existente, carregar dados
          if (scheduleId) {
            console.log("🔄 Carregando agendamento existente...");
            const { data } = await api.get(`/schedules/${scheduleId}`);
            
            // Verificar se o componente ainda está montado
            if (!isMounted.current) return;
            
            setSchedule((prevState) => ({
              ...prevState,
              ...data,
              sendAt: moment(data.sendAt).format("YYYY-MM-DDTHH:mm"),
              reminderDate: data.reminderDate ? moment(data.reminderDate).format("YYYY-MM-DDTHH:mm") : "",
            }));

            if (data.whatsapp) {
              setSelectedWhatsapps(data.whatsapp.id);
            }

            if (!isAdmin && data.ticketUser) {
              setSelectedUser(data.ticketUser);
            }

            if (data.queueId) {
              setSelectedQueue(data.queueId);
            }

            if (data.intervalo) {
              setIntervalo(data.intervalo);
            }

            if (data.tipoDias) {
              setTipoDias(data.tipoDias);
            }

            setCurrentContact(data.contact);
            console.log("✅ Agendamento carregado");
          }
        } catch (err) {
          console.error("❌ Erro ao carregar dados iniciais:", err);
          
          // Verificar se o componente ainda está montado antes de mostrar erro
          if (!isMounted.current) return;
          
          if (err.message === "Network Error") {
            setNetworkError(true);
            errorToast("Erro de conexão: Verifique se o servidor está rodando");
          } else {
            setNetworkError(false);
            toastError(err);
          }
        }
      };

      loadData();
    }
  }, [open, contactId, companyId, scheduleId, isAdmin]); // Removido findAllQueues das dependências

  // Carregar WhatsApps
  useEffect(() => {
    if (open) {
      console.log("🔄 Carregando WhatsApps...");
      api
        .get(`/whatsapp/filter`, {
          params: { session: 0, channel: channelFilter },
        })
        .then(({ data }) => {
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));

          setWhatsapps(mappedWhatsapps);
          console.log("✅ WhatsApps carregados:", mappedWhatsapps.length);
          // Removida seleção automática - usuário deve selecionar manualmente
        })
        .catch((err) => {
          console.error("❌ Erro ao carregar WhatsApps:", err);
          if (err.message === "Network Error") {
            setNetworkError(true);
            errorToast("Erro de conexão: Não foi possível carregar as conexões WhatsApp");
          } else {
            setNetworkError(false);
            toastError(err);
          }
          setWhatsapps([]); // Define array vazio em caso de erro
        });
    }
  }, [open, channelFilter]);

  // Buscar quickMessages quando o modal abrir
  useEffect(() => {
    if (open && user?.companyId) {
      fetchQuickMessages();
    }
  }, [open, user?.companyId, user?.id]);

  // Filtrar respostas rápidas baseado na busca
  useEffect(() => {
    if (quickMessageSearch.trim() === "" && !showAllQuickMessages) {
      setFilteredQuickMessages([]);
    } else if (quickMessageSearch.trim() === "" && showAllQuickMessages) {
      setFilteredQuickMessages(quickMessages);
    } else {
      const filtered = quickMessages.filter(qm => 
        qm.message.toLowerCase().includes(quickMessageSearch.toLowerCase()) ||
        qm.shortcode.toLowerCase().includes(quickMessageSearch.toLowerCase())
      );
      setFilteredQuickMessages(filtered);
    }
  }, [quickMessageSearch, quickMessages, showAllQuickMessages]);

  // Buscar usuários para seleção
  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          console.log("🔄 Buscando usuários...");
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
          console.log("✅ Usuários carregados:", data.users.length);
        } catch (err) {
          console.error("❌ Erro ao buscar usuários:", err);
          setLoading(false);
          if (err.message === "Network Error") {
            errorToast("Erro de conexão: Não foi possível carregar os usuários");
          } else {
            toastError(err);
          }
          setOptions([]); // Define array vazio em caso de erro
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  // Atualizar data quando selectedDate mudar
  useEffect(() => {
    if (selectedDate && open && !scheduleId) {
      try {
        console.log("📅 Atualizando data com selectedDate:", selectedDate);
        const selectedDateTime = new Date(selectedDate);
        const now = new Date();
        selectedDateTime.setHours(now.getHours() + 1);
        selectedDateTime.setMinutes(now.getMinutes());
        selectedDateTime.setSeconds(0);
        
        const newSendAt = moment(selectedDateTime).format("YYYY-MM-DDTHH:mm");
        console.log("⏰ Nova data de envio:", newSendAt);
        
        setSchedule(prevState => ({
          ...prevState,
          sendAt: newSendAt
        }));
      } catch (error) {
        console.error("Erro ao processar selectedDate:", error);
      }
    }
  }, [selectedDate, open, scheduleId]);

  const fetchQuickMessages = async () => {
    setLoadingQuickMessages(true);
    try {
      console.log("🔄 Buscando quickMessages com params:", {
        companyId: user?.companyId,
        userId: user?.id,
        isOficial: "false"
      });

      const { data } = await api.get("/quick-messages/list", {
        params: {
          companyId: user?.companyId,
          userId: user?.id,
          isOficial: "false"
        }
      });

      // Verificar se o componente ainda está montado antes de atualizar estado
      if (!isMounted.current) return;

      console.log("✅ Resposta da API quickMessages:", data);
      setQuickMessages(data || []);
      setFilteredQuickMessages(data || []);
    } catch (err) {
      console.error("❌ Erro ao buscar respostas rápidas:", err);
      
      // Verificar se o componente ainda está montado antes de mostrar erro
      if (!isMounted.current) return;
      
      if (err.message === "Network Error") {
        errorToast("Erro de conexão: Não foi possível carregar as respostas rápidas");
      } else {
        toastError(err);
      }
      setQuickMessages([]);
      setFilteredQuickMessages([]);
    } finally {
      // Verificar se o componente ainda está montado antes de atualizar estado
      if (isMounted.current) {
        setLoadingQuickMessages(false);
      }
    }
  };

  // Função para lidar com seleção de resposta rápida
  const handleQuickMessageSelect = async (selectedMessage, setFieldValue) => {
    console.log("🎯 Mensagem selecionada:", selectedMessage);
    
    if (selectedMessage) {
      console.log("✅ Preenchendo campo body com:", selectedMessage.message);
      setFieldValue("body", selectedMessage.message || "");
      setQuickMessageSearch(selectedMessage.message || "");
      
      // Fechar a lista de sugestões
      setShowAllQuickMessages(false);
      setFilteredQuickMessages([]);
    }
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
      default:
        return "error";
    }
  };

  const renderOption = (option) => {
    if (option.name) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography
            component="span"
            style={{
              fontSize: 14,
              marginLeft: "10px",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: "2",
            }}
          >
            {option.name}
          </Typography>
        </>
      );
    } else {
      return `Adicionar ${option.name}`;
    }
  };

  const handleSaveSchedule = async (values) => {
    console.log("🚀 handleSaveSchedule chamado com values:", values);
    console.log("📊 Estado atual - activeStep:", activeStep, "selectedWhatsapps:", selectedWhatsapps);
    
    const scheduleData = {
      ...values,
      userId: user.id,
      whatsappId: selectedWhatsapps,
      ticketUserId: selectedUser?.id || null,
      queueId: selectedQueue || null,
      intervalo: intervalo || 1,
      tipoDias: tipoDias || 4,
      reminderDate: values.reminderDate || null,
    };

    try {
      console.log("🔄 Salvando agendamento...", scheduleData);
      
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        console.log("✅ Agendamento atualizado");
      } else {
        await api.post("/schedules", scheduleData);
        console.log("✅ Agendamento criado");
      }

      // Verificar se o componente ainda está montado antes de atualizar estado
      if (!isMounted.current) return;

      successToast("🎉 Agendamento salvo com sucesso!");

      if (typeof reload == "function") {
        reload();
      }

      if (contactId && typeof cleanContact === "function") {
        cleanContact();
        history.push("/schedules");
      }
    } catch (err) {
      console.error("❌ Erro ao salvar agendamento:", err);
      
      // Verificar se o componente ainda está montado antes de mostrar erro
      if (!isMounted.current) return;
      
      if (err.message === "Network Error") {
        errorToast("🔌 Erro de conexão: Não foi possível salvar o agendamento. Verifique sua conexão com a internet.");
      } else if (err.response?.status === 400) {
        errorToast("⚠️ Dados inválidos: Verifique se todos os campos obrigatórios estão preenchidos.");
      } else if (err.response?.status === 401) {
        errorToast("🔐 Sessão expirada: Faça login novamente.");
      } else if (err.response?.status === 500) {
        errorToast("🔧 Erro interno do servidor: Tente novamente em alguns minutos.");
      } else {
        toastError(err);
      }
      
      // Não fecha o modal em caso de erro para permitir correção
      return;
    }

    // Só fecha o modal se o componente ainda estiver montado
    if (isMounted.current) {
      handleClose();
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 50)); // Reduzir delay de 100ms para 50ms
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const getStepContent = (step, values, setFieldValue, touched, errors) => {
    switch (step) {
      case 0:
        return (
          <div className={classes.stepContent}>
            <Typography className={classes.stepTitle}>
              <Person style={{ color: '#1976d2' }} />
              {i18n.t("scheduleModal.recipientTitle")}
            </Typography>
            <Typography className={classes.stepSubtitle}>
              {i18n.t("scheduleModal.recipientSubtitle")}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth style={{ marginBottom: '16px' }}>
                  <Autocomplete
                    fullWidth
                    value={currentContact}
                    options={contacts}
                    onChange={(e, contact) => {
                      const contactId = contact ? contact.id : "";
                      setSchedule({ ...schedule, contactId });
                      setCurrentContact(contact ? contact : initialContact);
                      setChannelFilter(contact ? contact.channel : "whatsapp");
                    }}
                    getOptionLabel={(option) => option.name}
                    renderOption={renderOption}
                    getOptionSelected={(option, value) => {
                      return value.id === option.id;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={i18n.t("scheduleModal.contactPlaceholder")}
                        label={i18n.t("scheduleModal.contactLabel")}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("scheduleModal.dateLabel")}
                  type="datetime-local"
                  name="sendAt"
                  error={touched.sendAt && Boolean(errors.sendAt)}
                  helperText={touched.sendAt && errors.sendAt}
                  variant="outlined"
                  fullWidth
                  style={{ marginBottom: '16px' }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </div>
        );

      case 1:
        return (
          <div className={classes.stepContent}>
            <Typography className={classes.stepTitle}>
              <FlashOn style={{ color: '#1976d2' }} />
              Mensagem
            </Typography>
            <Typography className={classes.stepSubtitle}>
              Digite a mensagem que será enviada ou use uma resposta rápida
            </Typography>
            
            <div style={{ marginBottom: '16px' }}>
              <Field
                as={TextField}
                rows={6}
                multiline={true}
                label="💬 Digite sua mensagem"
                name="body"
                inputRef={messageInputRef}
                error={touched.body && Boolean(errors.body)}
                helperText={touched.body && errors.body}
                variant="outlined"
                fullWidth
                placeholder="Digite a mensagem que será enviada..."
              />
            </div>

            {/* Campo de Respostas Rápidas */}
            <div style={{ marginBottom: '16px' }}>
              <TextField
                variant="outlined"
                fullWidth
                label="⚡ Respostas Rápidas"
                placeholder="Digite para buscar respostas rápidas..."
                value={quickMessageSearch}
                onChange={(e) => {
                  setQuickMessageSearch(e.target.value);
                  if (e.target.value.trim() === "") {
                    setShowAllQuickMessages(false);
                  }
                }}
                onFocus={() => {
                  if (quickMessageSearch.trim() === "" && !showAllQuickMessages) {
                    setShowAllQuickMessages(true);
                  }
                }}
                onClick={() => {
                  if (quickMessageSearch.trim() === "" && !showAllQuickMessages) {
                    setShowAllQuickMessages(true);
                  }
                }}
                disabled={loadingQuickMessages}
                InputProps={{
                  startAdornment: <FlashOn style={{ marginRight: '8px', color: '#1976d2' }} />,
                }}
              />
              
              {/* Lista de sugestões */}
              {((quickMessageSearch && filteredQuickMessages.length > 0) || (showAllQuickMessages && filteredQuickMessages.length > 0)) && (
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px', 
                  marginTop: '8px',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  position: 'relative'
                }}>
                  {filteredQuickMessages.map((quickMessage) => (
                    <div
                      key={quickMessage.id}
                      onClick={() => handleQuickMessageSelect(quickMessage, setFieldValue)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (e.target) {
                          e.target.style.backgroundColor = '#f5f7fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (e.target) {
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <FlashOn style={{ fontSize: '18px', color: '#1976d2' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#1976d2', marginBottom: '2px' }}>
                          {quickMessage.shortcode}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          maxWidth: '300px', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {quickMessage.message}
                        </div>
                      </div>
                      {quickMessage.mediaPath && (
                        <AttachFile style={{ fontSize: '16px', color: '#666' }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Mensagem quando não há resultados */}
              {quickMessageSearch && filteredQuickMessages.length === 0 && !loadingQuickMessages && (
                <div style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  color: '#666', 
                  fontStyle: 'italic',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px', 
                  marginTop: '8px'
                }}>
                  🔍 Nenhuma resposta rápida encontrada
                </div>
              )}
            </div>

            <MessageVariablesPicker
              disabled={false}
              showSchedulingVars={true}
              onClick={(value) => handleClickMsgVar(value, setFieldValue)}
            />
          </div>
        );

      case 2:
        return (
          <div className={classes.stepContent}>
            
            {/* Configurações básicas */}
            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth error={!selectedWhatsapps}>
                  <InputLabel>WhatsApp *</InputLabel>
                  <Select
                    value={selectedWhatsapps}
                    onChange={(event) => {
                      console.log("📱 WhatsApp selecionado:", event.target.value);
                      setSelectedWhatsapps(event.target.value);
                    }}
                    label="WhatsApp *"
                  >
                    {whatsapps && whatsapps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedWhatsapps && (
                    <Typography variant="caption" color="error" style={{ marginTop: '4px' }}>
                      Selecione uma conexão WhatsApp
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Abrir Ticket</InputLabel>
                  <Field
                    as={Select}
                    label="Abrir Ticket"
                    name="openTicket"
                    value={values.openTicket}
                    onChange={(e) => {
                      console.log("🎫 Abrir Ticket selecionado:", e.target.value);
                      setFieldValue("openTicket", e.target.value);
                    }}
                  >
                    <MenuItem value={"enabled"}>Habilitado</MenuItem>
                    <MenuItem value={"disabled"}>Desabilitado</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Status do Ticket</InputLabel>
                  <Field
                    as={Select}
                    disabled={values.openTicket === "disabled"}
                    label="Status do Ticket"
                    name="statusTicket"
                    value={values.statusTicket}
                    onChange={(e) => {
                      console.log("📊 Status Ticket selecionado:", e.target.value);
                      setFieldValue("statusTicket", e.target.value);
                    }}
                  >
                    <MenuItem value={"closed"}>Fechado</MenuItem>
                    <MenuItem value={"open"}>Aberto</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
            </Grid>

            {/* Seleção de Usuário e Fila */}
            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  variant="outlined"
                  className={classes.formControl}
                  getOptionLabel={(option) => `${option.name}`}
                  value={isAdmin ? selectedUser : user}
                  size="small"
                  onChange={(e, newValue) => {
                    if (isAdmin) {
                      setSelectedUser(newValue);
                      if (newValue != null && Array.isArray(newValue.queues)) {
                        if (newValue.queues.length === 1) {
                          setSelectedQueue(newValue.queues[0].id);
                        }
                        setQueues(newValue.queues);
                      } else {
                        setQueues(allQueues);
                        setSelectedQueue("");
                      }
                    }
                  }}
                  options={isAdmin ? options : [user]}
                  filterOptions={filterOptions}
                  freeSolo={isAdmin}
                  fullWidth
                  disabled={values.openTicket === "disabled" || !isAdmin}
                  autoHighlight
                  noOptionsText="Nenhuma opção"
                  loading={loading}
                  renderOption={(option) => (
                    <span>
                      <UserStatusIcon user={option} /> {option.name}
                    </span>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="👤 Usuário Responsável"
                      variant="outlined"
                      onChange={isAdmin ? (e) => setSearchParam(e.target.value) : undefined}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                        readOnly: !isAdmin,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>🏢 Fila</InputLabel>
                  <Select
                    value={selectedQueue}
                    onChange={(e) => setSelectedQueue(e.target.value)}
                    label="🏢 Fila"
                    disabled={values.openTicket === "disabled"}
                  >
                    {queues.map((queue) => (
                      <MenuItem key={queue.id} value={queue.id}>
                        {queue.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Switch para Assinar */}
            <div style={{ marginBottom: '16px' }}>
              <FormControlLabel
                control={
                  <Field
                    as={Switch}
                    color="primary"
                    name="assinar"
                    checked={values.assinar || false}
                    disabled={values.openTicket === "disabled"}
                  />
                }
                label="✍️ Assinar mensagem com nome do usuário"
              />
            </div>

            {/* Seção de Lembrete */}
            <div style={{ 
              backgroundColor: '#f8f9ff',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              border: '1px solid #e8ecf1'
            }}>
              <Typography variant="h6" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Schedule style={{ color: '#1976d2' }} />
                Lembrete (Opcional)
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                Defina uma data de lembrete. A mensagem será enviada no horário do lembrete.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    label="📅 Data do Lembrete"
                    type="datetime-local"
                    name="reminderDate"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary" style={{ 
                    padding: '16px', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: '8px', 
                    marginTop: '8px' 
                  }}>
                    💡 A mensagem do lembrete será a mesma da mensagem agendada.
                  </Typography>
                </Grid>
              </Grid>
            </div>

            {/* Seção de Recorrência */}
            <div style={{ 
              backgroundColor: '#fff8f0',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #ffeaa7',
              marginBottom: '16px'
            }}>
              <Typography variant="h6" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Repeat style={{ color: '#1976d2' }} />
                Recorrência
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                Configure se a mensagem deve ser enviada repetidamente.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Intervalo</InputLabel>
                    <Select
                      value={intervalo}
                      onChange={(e) => {
                        console.log("🔄 Intervalo selecionado:", e.target.value);
                        setIntervalo(e.target.value || 1);
                      }}
                      label="Intervalo"
                    >
                      <MenuItem value={1}>Dias</MenuItem>
                      <MenuItem value={2}>Semanas</MenuItem>
                      <MenuItem value={3}>Meses</MenuItem>
                      <MenuItem value={4}>Minutos</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    as={TextField}
                    label="Valor do Intervalo"
                    name="valorIntervalo"
                    variant="outlined"
                    fullWidth
                    placeholder="Ex: 1, 2, 3..."
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Field
                    as={TextField}
                    label="Quantas Vezes"
                    name="enviarQuantasVezes"
                    variant="outlined"
                    fullWidth
                    placeholder="Ex: 1, 5, 10..."
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Dias Úteis</InputLabel>
                    <Select
                      value={tipoDias}
                      onChange={(e) => {
                        console.log("📅 Dias úteis selecionado:", e.target.value);
                        setTipoDias(e.target.value || 4);
                      }}
                      label="Dias Úteis"
                    >
                      <MenuItem value={4}>Enviar normalmente</MenuItem>
                      <MenuItem value={5}>Enviar 1 dia útil antes</MenuItem>
                      <MenuItem value={6}>Enviar 1 dia útil depois</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </div>
          </div>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <div className={classes.root}>
      <ModernConfirmationModal
        title="Remover Anexo"
        message="Tem certeza que deseja remover este anexo? Esta ação não pode ser revertida."
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => {
          // Lógica de remoção do anexo aqui
          setConfirmationOpen(false);
        }}
        type="warning"
        confirmText={i18n.t("scheduleModal.buttons.okAdd")}
        cancelText={i18n.t("scheduleModal.buttons.cancel")}
      />
      
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="body"
        PaperProps={{
          style: {
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
            margin: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            position: "relative",
          }
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }
        }}
      >
        <DialogTitle style={{
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        }}>
          <Typography variant="h6" component="div" style={{ fontWeight: 600, textAlign: 'center' }}>
            📅 {scheduleId ? i18n.t('scheduleModal.title.edit') : i18n.t('scheduleModal.title.add')}
          </Typography>
          {networkError && (
            <div style={{
              backgroundColor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>🔌</span>
              <Typography variant="body2" style={{ color: '#c62828' }}>
                Problema de conexão detectado. Verifique sua internet e tente novamente.
              </Typography>
            </div>
          )}
        </DialogTitle>
        
        <Stepper activeStep={activeStep} className={classes.stepper} style={{
          padding: '24px 24px 16px 24px',
          backgroundColor: 'transparent',
          borderBottom: '1px solid #f0f0f0',
        }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>
                <div>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {step.label()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {step.description()}
                  </Typography>
                </div>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={(values, actions) => {
            console.log("🚀 Formik onSubmit - activeStep:", activeStep);
            // Só executa se estiver no último step
            if (activeStep === steps.length - 1) {
              console.log("✅ Executando handleSaveSchedule");
              setTimeout(() => {
                handleSaveSchedule(values);
                actions.setSubmitting(false);
              }, 400);
            } else {
              console.log("❌ Ignorando submit - não está no último step");
              actions.setSubmitting(false);
            }
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent style={{ 
                minHeight: '500px', 
                maxHeight: '60vh',
                padding: '0 24px',
                overflow: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#e0e0e0 transparent',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e0e0e0',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#bdbdbd',
                }
              }}>
                {getStepContent(activeStep, values, setFieldValue, touched, errors)}
              </DialogContent>
              
              <div className={classes.navigationButtons} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px',
                borderTop: '1px solid #f0f0f0',
                backgroundColor: '#fafafa',
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
              }}>
                <div>
                  {activeStep > 0 && (
                    <Button
                      onClick={handleBack}
                      className={classes.backButton}
                      variant="outlined"
                    >
                      Voltar
                    </Button>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("scheduleModal.buttons.cancel")}
                  </Button>
                  
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting || !isStepValid(activeStep, values)}
                      variant="contained"
                      className={`${classes.btnWrapper} ${classes.nextButton}`}
                      onClick={(e) => {
                        console.log("🎯 Botão Agendar clicado");
                        // O submit será tratado pelo onSubmit do Formik
                      }}
                    >
                      {scheduleId ? "💾 Atualizar" : "📅 Agendar"}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log("➡️ Avançando para próximo step...");
                        handleNext();
                      }}
                      disabled={!isStepValid(activeStep, values)}
                      variant="contained"
                      color="primary"
                      className={classes.nextButton}
                    >
                      {i18n.t("scheduleModal.buttons.next")}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default StepperScheduleModal;